import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import DialogFooter from './components/DialogFooter';
import { SELECTED_ORG_ID_STORAGE_KEY } from './constants';
import { setCookie } from './utils/cookies';
import type { PreviewLayoutUsageEntry } from './wysiwyg/previewLayouts';

type PreviewLayoutUsageModalProps = {
  show: boolean;
  layoutLabel: string;
  entries: PreviewLayoutUsageEntry[];
  loading: boolean;
  onRefresh: () => void;
  onCancel: () => void;
};

const formatPrintedDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const buildEditPath = (entry: PreviewLayoutUsageEntry): string | null => {
  if (entry.type === 'Template' && entry.templateId) {
    return `/templates/${entry.templateId}/design`;
  }
  if (entry.type === 'Print Run' && entry.printRunId) {
    return `/prints/${entry.printRunId}/edit`;
  }
  return null;
};

export default function PreviewLayoutUsageModal({
  show,
  layoutLabel,
  entries,
  loading,
  onRefresh,
  onCancel,
}: PreviewLayoutUsageModalProps) {
  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, show]);

  if (!show || typeof document === 'undefined') return null;

  const openEditItem = (entry: PreviewLayoutUsageEntry) => {
    const path = buildEditPath(entry);
    if (!path) return;

    try {
      setCookie('selectedOrgId', entry.organizationId);
      window.localStorage.setItem(SELECTED_ORG_ID_STORAGE_KEY, String(entry.organizationId));
    } catch {
      // A blocked storage write should not prevent the edit link from opening.
    }

    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Layout Cannot Be Deleted"
      data-testid="preview-layout-usage-modal"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        className="drawer-controls-card"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 860,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
          overflow: 'auto',
        }}
      >
        <div className="stack" style={{ gap: 12 }}>
          <div className="modal-title">Layout Cannot Be Deleted</div>
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
            This layout can&apos;t be deleted while it&apos;s active in a print run or set as a
            label&apos;s default (found in the “Print Options” of the label template editor). If
            you are positive this layout should be deleted, reassign the layout in the items
            below, then delete.
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Layout: <strong>{layoutLabel}</strong>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table" data-testid="preview-layout-usage-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Type</th>
                  <th>Template Name</th>
                  <th>Print Run - Package ID</th>
                  <th>Print Run - Print Date</th>
                  <th>Link To Edit Item</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="muted">Loading…</td></tr>
                )}
                {!loading && !entries.length && (
                  <tr><td colSpan={6} className="muted">No usages found.</td></tr>
                )}
                {!loading && entries.map((entry, index) => {
                  const path = buildEditPath(entry);
                  return (
                    <tr key={`${entry.type}-${entry.templateId ?? ''}-${entry.printRunId ?? ''}-${index}`}>
                      <td>{entry.organizationName || '—'}</td>
                      <td>{entry.type}</td>
                      <td>{entry.templateName || '—'}</td>
                      <td>{entry.type === 'Print Run' ? (entry.packageLabel || '—') : '—'}</td>
                      <td>{entry.type === 'Print Run' ? formatPrintedDate(entry.printedDate) : '—'}</td>
                      <td>
                        {path ? (
                          <button
                            type="button"
                            className="button secondary"
                            onClick={() => openEditItem(entry)}
                          >
                            Edit
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <button
              type="button"
              className="button secondary"
              onClick={onCancel}
              data-testid="preview-layout-usage-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="button"
              onClick={onRefresh}
              disabled={loading}
              data-testid="preview-layout-usage-refresh"
            >
              {loading ? 'Refreshing…' : 'Refresh List'}
            </button>
          </DialogFooter>
        </div>
      </div>
    </div>,
    document.body,
  );
}
